/**
 * 上下文大小优化工具
 * 提供智能调整文本上下文大小的功能
 *
 * 核心功能：
 * 1. 根据语言特性自动调整上下文大小限制
 * 2. 保留最相关的上下文内容
 * 3. 优化API调用的数据大小
 */
import { detectLanguage } from '../language/languageDetection.js';
/**
 * 判断是否为中日韩字符
 * @param char 字符
 * @returns 是否为中日韩字符
 */
function isCJK(char) {
    return /[\u4e00-\u9fa5\u3040-\u30ff\uac00-\ud7af]/.test(char);
}
/**
 * 智能截断文本，支持中日韩和英文字符分别设定上限
 * @param text 待截断文本
 * @param cjkLimit CJK字符上限
 * @param enLimit 英文字符上限
 * @param fromEnd 是否从末尾截断（默认false）
 * @returns 截断后的文本
 */
export function smartTruncate(text, cjkLimit, enLimit, fromEnd = false) {
    if (fromEnd) {
        text = text.split('').reverse().join('');
    }
    let cjkCount = 0, enCount = 0, result = '';
    let i = 0;
    for (; i < text.length; i++) {
        const char = text[i];
        if (isCJK(char)) {
            if (cjkCount >= cjkLimit)
                break;
            cjkCount++;
            result += char;
        }
        else {
            if (enCount >= enLimit)
                break;
            enCount++;
            result += char;
        }
    }
    // 英文超限后，补全单词直到遇到空格/标点/非英文字符/结尾
    if (enCount >= enLimit) {
        let j = i;
        while (j < text.length) {
            const char = text[j];
            // 如果遇到空格、标点或非英文字符，停止
            if (char === ' ' || /[.,!?;:]/.test(char) || !/[a-zA-Z]/.test(char))
                break;
            result += char;
            j++;
        }
    }
    if (fromEnd) {
        result = result.split('').reverse().join('');
    }
    return result;
}
/**
 * 精确提取选中词前后上下文（字符数限制，跨段落，自动语言检测）
 * @param allParagraphs 5个上下文段落
 * @param selectedParagraphIndexInAll 选中词所在段落在 allParagraphs 数组中的索引
 * @param selectionStartOffsetInParagraph 选中词在段落内的起始偏移量
 * @param selectionEndOffsetInParagraph 选中词在段落内的结束偏移量
 * @param sourceLang 源语言（可为'auto'，自动检测）
 * @returns { beforeText, afterText }
 */
export function getPreciseContextAroundSelection(allParagraphs, selectedParagraphIndexInAll, selectionStartOffsetInParagraph, selectionEndOffsetInParagraph, sourceLang = 'auto') {
    // 1. 语言检测
    let langForSizing = sourceLang;
    if (langForSizing === 'auto' && allParagraphs[selectedParagraphIndexInAll]) {
        langForSizing = detectLanguage(allParagraphs[selectedParagraphIndexInAll].content);
    }
    if (!langForSizing)
        langForSizing = 'en';
    // 2. 拼接 beforeText（不做字符数限制，最多1000字符防止极端超长）
    let beforeText = '';
    let paraIdx = selectedParagraphIndexInAll;
    let offset = selectionStartOffsetInParagraph;
    let totalLen = 0;
    while (paraIdx >= 0 && totalLen < 1000) {
        const para = allParagraphs[paraIdx].content;
        const end = paraIdx === selectedParagraphIndexInAll ? offset : para.length;
        const start = 0;
        const fragment = para.substring(start, end);
        beforeText = fragment + beforeText;
        totalLen += fragment.length;
        paraIdx--;
        offset = allParagraphs[paraIdx]?.content.length ?? 0;
    }
    beforeText = smartTruncate(beforeText, 50, 200, true);
    // 3. 拼接 afterText（不做字符数限制，最多1000字符防止极端超长）
    let afterText = '';
    paraIdx = selectedParagraphIndexInAll;
    offset = selectionEndOffsetInParagraph;
    totalLen = 0;
    while (paraIdx < allParagraphs.length && totalLen < 1000) {
        const para = allParagraphs[paraIdx].content;
        const start = paraIdx === selectedParagraphIndexInAll ? offset : 0;
        const end = para.length;
        const fragment = para.substring(start, end);
        afterText += fragment;
        totalLen += fragment.length;
        paraIdx++;
        offset = 0;
    }
    afterText = smartTruncate(afterText, 50, 200);
    return { beforeText, afterText };
}
//# sourceMappingURL=sizing.js.map